"use client";

import { useMemo, useState } from "react";
import dynamic from "next/dynamic";
import {
    Button,
    Dropdown as ControlledDropdown,
    DropdownItem,
    DropdownList,
    Heading,
    Icon,
} from "@farm-junction/design-library";
import CarVariantColorCard, {
    type CarVariantColorItem,
} from "@/component/ui/Cards/CarVariantColorCard";
import type { ColourGalleryVariant, ModelColour } from "./ModelColourShowcaseLib";
import type { ColourGalleryImage } from "./ColourGalleryPopup";
import type { GallerySubcategory } from "@/component/pdp/image-page/types";

const ColourGalleryPopup = dynamic(() => import("./ColourGalleryPopup"));

const INITIAL_VISIBLE = 6;
const LOAD_MORE_COUNT = 6;

export type SelectColorVariantOption = {
    id: string;
    name: string;
};

export type SelectColorWithVariantProps = {
    brandName: string;
    modelName: string;
    bodyType?: string;
    colours: CarVariantColorItem[];
    variants: SelectColorVariantOption[];
    images?: string[];
    galleryImages?: ColourGalleryImage[];
    gallerySubcategories?: GallerySubcategory[];
    galleryVariants?: ColourGalleryVariant[];
    modelId?: string;
    variantId?: string | null;
    isUpcoming?: boolean;
};

export default function SelectColorWithVariant({
    brandName,
    modelName,
    bodyType,
    colours,
    variants,
    images = [],
    galleryImages,
    gallerySubcategories,
    galleryVariants = [],
    modelId,
    variantId,
    isUpcoming = false,
}: SelectColorWithVariantProps) {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [selectedVariantId, setSelectedVariantId] = useState("");
    const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);
    const [selectedColourId, setSelectedColourId] = useState(colours[0]?.id ?? "");
    const [isPopupOpen, setIsPopupOpen] = useState(false);

    const filteredColours = useMemo(() => {
        if (!selectedVariantId) return colours;
        return colours.filter((colour) =>
            colour.variants.some((variant) => variant.id === selectedVariantId),
        );
    }, [colours, selectedVariantId]);

    const visibleColours = filteredColours.slice(0, visibleCount);
    const hasMore = visibleCount < filteredColours.length;
    const selectedVariant = variants.find((variant) => variant.id === selectedVariantId);
    const galleryColours: ModelColour[] = useMemo(
        () =>
            colours.map((colour) => ({
                id: colour.id,
                name: colour.name,
                hex: colour.hex,
                image: colour.image,
                variantCount: colour.variants.length || undefined,
                variantIds: colour.variants.map((variant) => variant.id),
            })),
        [colours],
    );
    const popupIndex = Math.max(
        0,
        galleryColours.findIndex((colour) => colour.id === selectedColourId),
    );

    if (!colours.length) return null;

    const selectVariant = (variantIdValue: string) => {
        setSelectedVariantId(variantIdValue);
        setVisibleCount(INITIAL_VISIBLE);
        setIsDropdownOpen(false);
        const nextColours = variantIdValue
            ? colours.filter((colour) => colour.variants.some((variant) => variant.id === variantIdValue))
            : colours;
        if (nextColours[0]) setSelectedColourId(nextColours[0].id);
    };

    const openColourPopup = (colourId: string) => {
        setSelectedColourId(colourId);
        setIsPopupOpen(true);
    };

    return (
        <section className="mb-4 font-sans md:mb-6" data-testid="select-color-with-variant">
            <Heading
                variant="h2"
                level={2}
                className="!mb-1 text-[20px] font-bold leading-7 text-[#16181D] md:text-[24px]"
            >
                {brandName} {modelName} Colours & Available Variants
            </Heading>
            <p className="text-[13px] text-[#737373] md:text-sm">
                {`The ${brandName} ${modelName} is offered in multiple colour options to suit different preferences. Explore the complete list of ${modelName} colours, view each shade on the ${bodyType || "car"}, and choose the one that best matches your style.`}
            </p>

            {variants.length > 0 && (
                <div className="mt-4 w-full md:max-w-[280px]">
                    <ControlledDropdown
                        trigger={
                            <Button
                                variant="secondary"
                                className="h-11 w-full justify-between rounded-xl border border-[#E5E7EB] bg-white px-4 text-[14px] font-medium text-[#16181D] hover:bg-white"
                            >
                                <span className="truncate">{selectedVariant?.name || "Select Variant"}</span>
                                <Icon name="downCaret" width={12} height={12} fill="#6B7280" />
                            </Button>
                        }
                        align="right"
                        sideOffset={4}
                        className="font-sans w-full"
                        open={isDropdownOpen}
                        onOpenChange={setIsDropdownOpen}
                    >
                        <DropdownList className="max-h-64 min-w-[16rem] overflow-y-auto">
                            <DropdownItem onClick={() => selectVariant("")}>All Variants</DropdownItem>
                            {variants.map((variant) => (
                                <DropdownItem key={variant.id} onClick={() => selectVariant(variant.id)}>
                                    {variant.name}
                                </DropdownItem>
                            ))}
                        </DropdownList>
                    </ControlledDropdown>
                </div>
            )}

            <div className="mt-4 grid grid-cols-1 gap-3 md:mt-5 md:grid-cols-2 md:gap-4 lg:grid-cols-3">
                {visibleColours.map((colour) => (
                    <CarVariantColorCard
                        key={colour.id}
                        colour={colour}
                        selected={colour.id === selectedColourId}
                        onClick={() => openColourPopup(colour.id)}
                    />
                ))}
            </div>

            {hasMore && (
                <button
                    type="button"
                    onClick={() => setVisibleCount((count) => count + LOAD_MORE_COUNT)}
                    className="mt-4 flex h-12 w-full cursor-pointer items-center justify-center gap-2 rounded-xl border border-[#E5E7EB] bg-white text-[14px] font-medium text-[#16181D] hover:bg-[#F9FAFB] md:mt-5"
                >
                    Load More Colors
                    <Icon name="downCaret" width={12} height={12} fill="#6B7280" />
                </button>
            )}

            <ColourGalleryPopup
                open={isPopupOpen}
                onClose={() => setIsPopupOpen(false)}
                brandName={brandName}
                modelName={modelName}
                colours={galleryColours}
                images={images}
                galleryImages={galleryImages}
                gallerySubcategories={gallerySubcategories}
                variants={galleryVariants}
                startIndex={popupIndex}
                initialTab="colors"
                onColourChange={(index) => {
                    const next = galleryColours[index];
                    if (next) setSelectedColourId(next.id);
                }}
                modelId={modelId}
                variantId={variantId}
                isUpcoming={isUpcoming}
            />
        </section>
    );
}
